import { useState } from 'react'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { verifyPhoneSignup } from '@/models/auth/ports'
import { breakpoints, fontWeights } from '@/styles'
import { isEmptyOrNull } from '@/utils'
import useMediaQuery from '@/hooks/use-media-query'
import usePasswordRequirements from '@/hooks/use-password-requirements'
import { PasswordRequirements } from '@/features/Auth/components'
import { show as showLoginModal } from '@/features/Auth/login-modal'
import AgreeToTerms from '@/components/agree-to-terms'
import { Button } from '@/components/button-new'
import {
  FieldInput,
  Group,
  PasswordInput,
  PhoneInput,
  Row
} from '@/components/form'
import { PhoneErrorIcon, RoundCheckWhiteIcon } from '@/components/icons'
import { showWarningModal } from '@/components/warning-modal'

import { useAccountForm } from './account-form-provider'
import { FormCard, FormTitle } from './elements'
import { show as showVerifyCodeSignupModal } from './verifyCodeSignupModal'

const SignUpWrapper = styled.div`
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    max-width: 320px;
    display: flex;
    flex-direction: column;
    align-self: center;
  }
`

const PhoneFormContainer = styled.div`
  width: 100%;
  padding-right: 10px;
  padding-left: 10px;
  gap: 0 8px;
  margin-top: 16px;
  display: flex;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
    gap: 16px 0;
    margin: 0 0 16px;
  }
`

const PhoneWrapper = styled.div`
  flex: 1;
`

const PhoneButtonWrapper = styled.div``

const StyledButton = styled(Button)`
  min-width: 124px;
  height: 60px;

  @media screen and (max-width: ${breakpoints.desktop}) {
    min-width: 172px;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: 48px;
  }
`

const VerifiedButton = styled(StyledButton)`
  background: #00cca3 !important;
`

const SubmitWrapper = styled.div`
  margin: 24px 0 0;
  > button {
    width: 100%;
  }
`

const PromoText = styled.div`
  font-size: 16px;
  line-height: 24px;
  font-weight: ${fontWeights.regular};
  font-family: 'Inter';
  margin-bottom: 24px;
  display: inline-block;
`

const PromoCodeText = styled.p`
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  font-family: 'Inter';
  margin: 0px;
  color: #2b8f65;
  display: inline-block;
`

const AccountSection = ({ pageId }) => {
  const {
    values,
    setFieldValue,
    isSubmitting,
    handlePhoneVerify,
    isValid: isFormValid,
    errors
  } = useAccountForm()
  const loading = isSubmitting
  const disabled = isSubmitting || !isFormValid
  const router = useRouter()
  const { t } = useTranslation()
  const { query } = router
  const promoCode = query.couponCode
  const [verifiedNumber, setVerifiedNumber] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [phoneSubmitting, setPhoneSubmitting] = useState(false)
  const { requirements, checkRequirements } = usePasswordRequirements()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const colSize = displayMobile ? 12 : 6
  const verifyButtonSize = displayMobile ? '100%' : '50%'
  const gapSize = displayMobile ? '0' : 16
  const mobileGapSize = displayMobile ? 16 : '0'

  const userVerified = phone => {
    setVerifiedNumber(phone)
  }

  const onPhoneSubmit = async phone => {
    setPhoneSubmitting(true)
    const { response, body } = await verifyPhoneSignup({
      phone
    })

    if (response.ok) {
      showVerifyCodeSignupModal({ phone, userVerified })
    } else if (body.message === 'Phone number already exists.') {
      showWarningModal({
        title: t('b.auth.create.existing_phone.title', 'Existing phone number'),
        message: t(
          'b.auth.create.phone_taken.text',
          'This phone number is already linked to a liv.rent account, please log in.'
        ),
        icon: PhoneErrorIcon,
        button: {
          text: t('common.login', 'Log In'),
          onClick: () =>
            showLoginModal({
              eventId: 'web-signup-submit-landlord-phonetaken',
              t
            })
        },
        secondaryButton: {
          text: t('b.auth.create.try_again.button', 'Try Again')
        }
      })
    } else {
      showWarningModal({
        title: t(
          'b.auth.create.general_error_phone.title',
          'Oops! Something went wrong'
        ),
        message: body.message || body.phone,
        icon: PhoneErrorIcon,
        secondaryButton: {
          text: t('b.auth.create.try_again.button', 'Try Again')
        }
      })
    }
    setPhoneSubmitting(false)
  }
  const shouldDisable = (isValid, currNumber) => {
    const isEmpty = isEmptyOrNull(currNumber)
    if (isEmpty || !isValid || phoneSubmitting) return true
    if (!isEmpty) {
      const prevVerified = currNumber.toString() === verifiedNumber
      handlePhoneVerify(prevVerified)
      return prevVerified
    }
    return false
  }

  const isPhoneVerified = values.phone === verifiedNumber && values.phone
  const isValid = !errors.phone
  const isVerifyDisabled = shouldDisable(isValid, values.phone)

  return (
    <>
      <FormTitle text={t('b.auth.view.account_info.title', 'Account Info')} />
      <FormCard>
        <SignUpWrapper>
          {!!promoCode && (
            <Trans i18nKey="pricing:coupon code text" promoCode={promoCode}>
              <PromoText>
                Your coupon code <PromoCodeText>{{ promoCode }}</PromoCodeText>{' '}
                will be automatically applied at checkout.
              </PromoText>
            </Trans>
          )}
          <Row top={0}>
            <Group col={colSize} bottom={mobileGapSize}>
              <FieldInput
                name="first_name"
                autoComplete="section-register-landlord given-name"
                id="first_name"
                required
                placeholder={t('common.first_name', 'First name')}
              />
            </Group>
            <Group col={colSize} bottom={mobileGapSize}>
              <FieldInput
                name="last_name"
                autoComplete="section-register-landlord family-name"
                id="last_name"
                required
                placeholder={t('common.last_name', 'Last name')}
              />
            </Group>
          </Row>
          <Row top={gapSize}>
            <Group col={colSize} bottom={mobileGapSize}>
              <FieldInput
                name="preferred_name"
                autoComplete="section-register-landlord nickname"
                id="preferred_name"
                placeholder={t(
                  'b.auth.create.preferred_name.label',
                  'Preferred name'
                )}
              />
            </Group>
            <Group col={colSize} bottom={mobileGapSize}>
              <FieldInput
                name="email"
                autoComplete="section-register-landlord username email"
                id="email"
                type="email"
                required
                placeholder={t('common.email', 'Email')}
              />
            </Group>
          </Row>
          <Row>
            <PhoneFormContainer>
              <PhoneWrapper>
                <PhoneInput
                  name="phone"
                  autoComplete="section-register-landlord tel"
                  id="phone"
                  placeholder={t(
                    'b.auth.view.phone_number.placeholder',
                    'Phone number'
                  )}
                  value={values.phone}
                />
              </PhoneWrapper>
              <PhoneButtonWrapper>
                {isPhoneVerified ? (
                  <VerifiedButton
                    type="button"
                    width={verifyButtonSize}
                    theme="success"
                    disabled
                  >
                    <RoundCheckWhiteIcon width={24} height={24} />
                  </VerifiedButton>
                ) : (
                  <StyledButton
                    id="web-phone-code-verification-button-landlord"
                    type="button"
                    width={verifyButtonSize}
                    theme="primary"
                    disabled={isVerifyDisabled}
                    onClick={event => {
                      event.preventDefault()
                      onPhoneSubmit(values.phone)
                    }}
                  >
                    {t('b.auth.view.verify_phone_button.text', 'Verify')}
                  </StyledButton>
                )}
              </PhoneButtonWrapper>
            </PhoneFormContainer>
          </Row>
          <Row top={gapSize}>
            <Group col={colSize}>
              <PasswordInput
                name="password"
                autoComplete="section-register-landlord new-password"
                id="password"
                placeholder={t(
                  'b.auth.create.enter_password.label',
                  'Enter password'
                )}
                onChange={e => {
                  setFieldValue('password', e.target.value)
                  checkRequirements(e.target.value)
                }}
                visible={showPassword}
                setVisible={setShowPassword}
                hideErrorMessage
              />
            </Group>
            <Group col={colSize}>
              <PasswordInput
                name="password_confirm"
                autoComplete="section-register-landlord new-password"
                id="password_confirm"
                placeholder={t(
                  'b.auth.create.confirm_password.label',
                  'Confirm password'
                )}
                visible={showPassword}
                hideErrorMessage
              />
            </Group>
          </Row>
          <PasswordRequirements requirements={requirements} show={true} />
          <Row>
            <AgreeToTerms top={0} t={t} />
          </Row>
          <SubmitWrapper>
            <Button
              type="submit"
              theme="primary"
              id={`landlord-${pageId}-${
                values.intended_plan || 'essentials'
              }-createaccount-click`}
              disabled={disabled}
              loading={loading}
            >
              {t('pricing:Next')}
            </Button>
          </SubmitWrapper>
        </SignUpWrapper>
      </FormCard>
    </>
  )
}

export default AccountSection
